import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, Clock, CheckCircle2, ChevronLeft, ChevronRight, Users } from "lucide-react";
import PublicPage from "@/components/PublicPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const TEAM_SIZES = ["1-10", "11-50", "51-200", "200+"];
const BLANK = { name: "", company: "", email: "", phone: "", team_size: "11-50", notes: "" };

const label = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });

export default function BookDemo() {
  const [days, setDays] = useState([]);
  const [dayIdx, setDayIdx] = useState(0);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () =>
    api
      .get("/public/demo/slots")
      .then(({ data }) => setDays(data.days))
      .catch(() => toast.error("Could not load the demo calendar"));

  useEffect(() => { load(); }, []);

  const day = days[dayIdx];
  const week = days.slice(Math.floor(dayIdx / 6) * 6, Math.floor(dayIdx / 6) * 6 + 6);

  const book = async (e) => {
    e.preventDefault();
    if (!slot || !day) { toast.error("Please pick a 30-minute slot first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/public/demo/book", { ...form, date: day.date, time: slot });
      setConfirmed(data);
      toast.success("Demo confirmed — confirmation email sent");
    } catch (err) {
      const detail = apiError(err.response?.data?.detail);
      toast.error(detail || "Could not book that slot");
      if (err.response?.status === 409) { setSlot(null); load(); }
    } finally {
      setBusy(false);
    }
  };

  if (confirmed) {
    return (
      <PublicPage testId="demo-page" title="Your demo is confirmed" updated={false}>
        <div className="border border-emerald-500/40 bg-emerald-500/10 p-6" data-testid="demo-success">
          <CheckCircle2 className="w-7 h-7 text-emerald-500" />
          <p className="text-lg font-semibold mt-3 num" data-testid="demo-confirmed-slot">
            {label(confirmed.date)} · {confirmed.time} IST
          </p>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Reference <strong className="num text-foreground" data-testid="demo-reference">{confirmed.reference}</strong>. A confirmation
            email is on its way with everything we will cover. We will call you on the number you gave us, so keep a
            live BOQ or work-front list handy — we can run it through the platform during the session.
          </p>
          <div className="flex flex-wrap gap-2.5 mt-5">
            <Link to="/signup"><Button size="sm" data-testid="demo-trial-btn">Start the free trial meanwhile</Button></Link>
            <Link to="/features"><Button size="sm" variant="outline" data-testid="demo-features-btn">See the platform</Button></Link>
          </div>
        </div>
      </PublicPage>
    );
  }

  return (
    <PublicPage
      testId="demo-page"
      title="Book a live demo"
      updated={false}
      meta={{
        title: "Book a Live Demo — Plan-Sarthi EPC Project Management Software",
        description:
          "Pick a 30-minute IST slot and see Plan-Sarthi on a real EPC project package — DPR to S-curve, critical path, earned value and Risk Radar. No card required.",
        path: "/demo",
      }}
      intro="Thirty minutes, one planning engineer to another. We open a real project package and show how a Daily Progress Report recomputes the S-curve, the critical path, CPI/SPI and the forecast completion date — then answer whatever your team throws at us."
    >
      <section data-testid="demo-calendar">
        <h2 className="text-base md:text-lg font-semibold flex items-center gap-2">
          <CalendarCheck className="w-4 h-4 text-[#F97316]" /> Pick a date
        </h2>
        <p className="text-[12.5px] text-muted-foreground mt-1.5">
          All slots are 30 minutes, Indian Standard Time. Sundays are closed.
        </p>

        <div className="flex items-center gap-2 mt-4">
          <Button
            size="icon"
            variant="outline"
            className="h-9 w-9 shrink-0"
            disabled={dayIdx < 6}
            onClick={() => setDayIdx(Math.max(0, dayIdx - 6))}
            data-testid="demo-prev-week-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1 grid grid-cols-3 sm:grid-cols-6 gap-2">
            {week.map((d) => {
              const free = d.slots.filter((s) => s.available).length;
              const active = day?.date === d.date;
              return (
                <button
                  key={d.date}
                  onClick={() => { setDayIdx(days.findIndex((x) => x.date === d.date)); setSlot(null); }}
                  className={cn(
                    "border px-2 py-2.5 text-center transition-colors duration-150",
                    active ? "border-[#F97316] bg-[#F97316]/10" : "border-border hover:border-[#F97316]/60",
                    !free && "opacity-40"
                  )}
                  disabled={!free}
                  data-testid={`demo-day-${d.date}`}
                >
                  <div className="text-[12px] font-medium">{label(d.date)}</div>
                  <div className="text-[10.5px] text-muted-foreground mt-0.5">{free ? `${free} free` : "full"}</div>
                </button>
              );
            })}
          </div>
          <Button
            size="icon"
            variant="outline"
            className="h-9 w-9 shrink-0"
            disabled={Math.floor(dayIdx / 6) * 6 + 6 >= days.length}
            onClick={() => setDayIdx(Math.min(days.length - 1, Math.floor(dayIdx / 6) * 6 + 6))}
            data-testid="demo-next-week-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <h2 className="text-base md:text-lg font-semibold flex items-center gap-2 mt-8">
          <Clock className="w-4 h-4 text-[#F97316]" /> Pick a 30-minute slot
        </h2>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-4" data-testid="demo-slot-grid">
          {(day?.slots || []).map((s) => (
            <button
              key={s.time}
              disabled={!s.available}
              onClick={() => setSlot(s.time)}
              className={cn(
                "num border px-2 py-2.5 text-[12.5px] transition-colors duration-150",
                slot === s.time ? "border-[#F97316] bg-[#F97316] text-white" : "border-border hover:border-[#F97316]/60",
                !s.available && "opacity-35 line-through cursor-not-allowed"
              )}
              data-testid={`demo-slot-${s.time.replace(":", "")}`}
            >
              {s.time}
            </button>
          ))}
          {!days.length ? <p className="col-span-full text-[12.5px] text-muted-foreground">Loading calendar…</p> : null}
        </div>
      </section>

      <form onSubmit={book} className="space-y-4 border-t border-border pt-8" data-testid="demo-form">
        <h2 className="text-base md:text-lg font-semibold flex items-center gap-2">
          <Users className="w-4 h-4 text-[#F97316]" /> Who is joining?
        </h2>
        {slot && day ? (
          <p className="text-[12.5px] text-[#F97316]" data-testid="demo-selected-slot">
            Selected: {label(day.date)} at {slot} IST (30 minutes)
          </p>
        ) : (
          <p className="text-[12.5px] text-muted-foreground">Pick a date and slot above, then fill this in.</p>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="d-name">Your name</Label>
            <Input id="d-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="R. Sharma" data-testid="demo-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="d-company">Company</Label>
            <Input id="d-company" required minLength={2} value={form.company} onChange={set("company")} placeholder="Sharma Infra Pvt Ltd" data-testid="demo-company-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="d-email">Work email</Label>
            <Input id="d-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="demo-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="d-phone">Phone</Label>
            <Input id="d-phone" required minLength={6} value={form.phone} onChange={set("phone")} placeholder="+91 98xxxxxxxx" data-testid="demo-phone-input" />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Project team size</Label>
          <Select value={form.team_size} onValueChange={(v) => setForm({ ...form, team_size: v })}>
            <SelectTrigger data-testid="demo-team-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TEAM_SIZES.map((s) => <SelectItem key={s} value={s} data-testid={`demo-team-${s}`}>{s} people</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="d-notes">What should we focus on? (optional)</Label>
          <Textarea
            id="d-notes"
            rows={4}
            value={form.notes}
            onChange={set("notes")}
            placeholder="Two thermal packages and one substation. Biggest pain is monthly client reporting and hindrance claims."
            data-testid="demo-notes-input"
          />
        </div>

        <Button type="submit" disabled={busy || !slot} className="w-full sm:w-auto" data-testid="demo-submit-btn">
          <CalendarCheck className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Confirming…" : "Confirm my demo"}
        </Button>
        <p className="text-[11.5px] text-muted-foreground">
          No payment, no obligation. We will email an instant confirmation and use your details only for this demo —
          see our <Link to="/privacy-policy" className="text-[#F97316] hover:underline">Privacy Policy</Link>.
        </p>
      </form>
    </PublicPage>
  );
}
